/* Recipe V2 API data types */

export enum RecipeStatus {
  Draft = 'Draft',
  Active = 'Active',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Expired = 'Expired',
}

export enum AllergenStatus {
  Contains = 'Contains',
  MayContain = 'MayContain',
  FreeFrom = 'FreeFrom',
}

export enum AllergenSource {
  Manual = 'Manual',
  Feed = 'Feed',
  AI = 'AI',
}

export enum DietaryCategory {
  Allergen = 'Allergen',
  Intolerance = 'Intolerance',
  DietaryRequirement = 'DietaryRequirement',
  LifestylePreference = 'LifestylePreference',
  NutritionalClaim = 'NutritionalClaim',
}

export enum DietaryAttributeStatus {
  Contains = 'Contains',
  MayContain = 'MayContain',
  FreeFrom = 'FreeFrom',
  Suitable = 'Suitable',
  NotSuitable = 'NotSuitable',
  Certified = 'Certified',
}

export interface DietaryAttribute {
  id: number;
  code: string;
  name: string;
  category: DietaryCategory;
  status: DietaryAttributeStatus;
  source: AllergenSource;
  iconUrl?: string;
  regulatory?: boolean;
}

export enum ApprovalStatus {
  Draft = 'Draft',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum AIFindingSeverity {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export enum AIFindingStatus {
  Open = 'Open',
  Dismissed = 'Dismissed',
  Acknowledged = 'Acknowledged',
}

export enum CarbonRating {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
}

/**
 * Standard recipes are sold/served as-is. Batch recipes (e.g. house sauces, doughs) are
 * prepared in bulk and held as stock - see `ConvertToStockRequest`/`convertToStock`.
 */
export enum RecipeKind {
  Standard = 'Standard',
  Batch = 'Batch',
}

export interface RecipeV2 {
  id: number;
  franchiseId: number;
  itemNumber: string;
  description: string;
  status: RecipeStatus;
  recipeType: RecipeKind;
  primaryCategory: string;
  yieldQuantity: number;
  yieldUom: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  productionMethodId: number | null;
  carbonScore: CarbonRating | null;
  totalCost: number;
  costPerPortion: number;
  allergenSummary: RecipeAllergenSummary[];
  dietaryAttributes: DietaryAttribute[];
  aiIssueCount: number;
  currentVersion: number;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface RecipeComponent {
  id: number;
  recipeId: number;
  componentProductId: number;
  componentItemNumber: string;
  componentDescription: string;
  quantity: number;
  weight: number;
  volume: number;
  sortOrder: number;
  uom: string;
  costPrice: number;
  costContribution: number;
  weightNet: number;
  isRecipe: boolean;
  children?: RecipeComponent[];
  allergens?: ProductAllergen[];
  carbonFactor?: CarbonFactor | null;
}

export interface RecipeAllergenSummary {
  allergenId: number;
  allergenCode: string;
  allergenName: string;
  status: AllergenStatus;
  source: AllergenSource;
  introducedBy: AllergenTraceEntry[];
}

export interface AllergenTraceEntry {
  componentId: number;
  componentDescription: string;
  depth: number;
  path: string;
}

export interface Allergen {
  id: number;
  code: string;
  name: string;
  iconUrl: string;
}

export interface ProductAllergen {
  productId: number;
  allergenId: number;
  allergenCode: string;
  allergenName: string;
  status: AllergenStatus;
  source: AllergenSource;
  updatedBy: string;
  updatedAt: string;
}

export interface CarbonFactor {
  productId: number;
  kgCo2ePerUnit: number;
  source: string;
  sourceReference: string;
  lastUpdated: string;
}

export interface ProductionMethod {
  id: number;
  name: string;
  carbonMultiplier: number;
  description: string;
}

export interface RecipeVersion {
  id: number;
  recipeId: number;
  versionNumber: number;
  changeDescription: string;
  changedBy: string;
  changedAt: string;
  approvalStatus: ApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  componentSnapshot: RecipeComponent[];
}

export interface AIFinding {
  id: number;
  recipeId: number;
  findingType: string;
  severity: AIFindingSeverity;
  description: string;
  confidence: number;
  status: AIFindingStatus;
  dismissedBy: string | null;
  dismissedReason: string | null;
  createdAt: string;
}

export interface RecipeV2Detail extends RecipeV2 {
  components: RecipeComponent[];
  allergens: RecipeAllergenSummary[];
  versions: RecipeVersion[];
  aiFindings: AIFinding[];
  carbonTotal: number;
  carbonPerPortion: number;
  productionMethod: ProductionMethod | null;
  steps: RecipeStep[];
}

export interface RecipeV2ListResponse {
  data: RecipeV2[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CostBreakdownEntry {
  componentId: number;
  componentDescription: string;
  costPrice: number;
  quantity: number;
  totalCost: number;
  percentageOfTotal: number;
}

export interface CostSimulationResult {
  originalCost: number;
  simulatedCost: number;
  difference: number;
  percentageChange: number;
  affectedRecipes: number;
}

export interface SupplierPrice {
  supplierId: number;
  supplierName: string;
  itemPrice: number;
  packSize: number;
  packUom: string;
  costPerUnit: number;
  isPreferred: boolean;
}

export interface ComponentSupplierComparison {
  componentId: number;
  componentDescription: string;
  suppliers: SupplierPrice[];
}

export interface CreateRecipeRequest {
  description: string;
  itemNumber: string;
  primaryCategory: string;
  yieldQuantity: number;
  yieldUom: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  productionMethodId: number | null;
  components: CreateComponentRequest[];
}

export interface CreateComponentRequest {
  componentProductId: number;
  componentItemNumber: string;
  componentDescription: string;
  quantity: number;
  weight: number;
  volume: number;
  uom: string;
  sortOrder: number;
}

export interface ApproveRejectRequest {
  comment: string;
}

export interface AllergenOverride {
  allergenId: number;
  overrideStatus: AllergenStatus;
  reason: string;
}

export interface RecipeExportRequest {
  recipeIds: number[];
  format: 'csv' | 'xlsx';
}

export interface RecipeBulkImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface StockItemSearchResult {
  id: number;
  itemNumber: string;
  description: string;
  uom: string;
  costPrice: number;
  weightNet: number;
  /** True when this result is a recipe (sub-recipe) rather than a raw stock item. */
  isRecipe?: boolean;
}

export interface ConvertToStockRequest {
  quantity: number;
  uom: string;
}

export interface ConvertToStockResult {
  stockItemNumber: string;
  quantity: number;
  uom: string;
}

export interface Equipment {
  id: number;
  name: string;
  description?: string;
}

export interface RecipeStep {
  id: number;
  recipeId: number;
  stepNumber: number;
  title: string;
  instruction: string;
  productionMethodId: number | null;
  durationMinutes: number | null;
  temperature: string | null;
  equipment: Equipment[];
}

export interface CreateUpdateStepRequest {
  stepNumber: number;
  title: string;
  instruction: string;
  productionMethodId: number | null;
  durationMinutes: number | null;
  temperature: string | null;
  equipmentIds: number[];
}

export interface UpdateComponentRequest {
  quantity: number;
  weight: number;
  volume: number;
  uom: string;
  sortOrder: number;
}

export interface UpdateRecipeMetadataRequest {
  description?: string;
  primaryCategory?: string;
  yieldQuantity?: number;
  yieldUom?: string;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  productionMethodId?: number | null;
}

export interface DietaryAttributeOverride {
  attributeId: number;
  status: DietaryAttributeStatus;
  reason: string;
}
